import React, { useState, useEffect } from "react";
import { Button } from "@/components/Calendar/ui/button";
import {
  Trash2,
  FileText,
  AlertTriangle,
  Send,
  Clock,
  User,
  MapPin,
  CheckCircle2,
  Syringe,
  Play,
  UserX,
  Edit,
  Building2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Calendar/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/Calendar/ui/hover-card";
import {
  type UseScheduleDataReturn,
  type Booking,
  useScheduleData,
} from "@/components/Calendar/scheduleData";
import { patientService } from "@/services/patientService";

interface BookingCardProps {
  booking: Booking;
  serviceColors: Record<string, string>;
  serviceNameMap?: Record<string, string>;
  serviceColorMap?: Record<string, string>;
  onEdit: (bookingId: string, bookingData: any) => void;
  onDelete: (bookingId: string) => void;
  onStartConsultation?: (booking: Booking) => void;
  pharmacists: string[];
  rooms: string[];
  size?: "micro" | "small" | "medium" | "large" | "accordion";
  isDarkMode?: boolean;
  onDragStart?: (booking: Booking) => void;
  onDurationChange?: (bookingId: string, newDuration: number) => void;
  enableResize?: boolean;
  showNotification?: boolean;
  notificationType?: "message" | "check";
  // Pulses a border/glow in this booking's own service color - used to point
  // out a specific appointment (e.g. one clicked from the dashboard) without
  // opening anything, since a plain checkmark flash doesn't say "this one".
  pulseHighlight?: boolean;
  pharmacyRooms: any[];
}

export function BookingCard({
  booking,
  serviceColors,
  serviceNameMap = {},
  serviceColorMap = {},
  onEdit,
  onDelete,
  onStartConsultation,
  pharmacists,
  rooms,
  size = "medium",
  isDarkMode = false,
  onDragStart,
  onDurationChange,
  enableResize = false,
  showNotification = false,
  notificationType = "check",
  pulseHighlight = false,
}: BookingCardProps) {
  const [showPreConsult, setShowPreConsult] = useState(false);
  const [isPartialMatch, setIsPartialMatch] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartY, setResizeStartY] = useState(0);
  const [tempDuration, setTempDuration] = useState(booking.duration);
  const justDraggedRef = React.useRef(false);
  const s = useScheduleData();
  const { pharmacyRooms, handleStartConsultation, handleMarkAsNoShow } = (s || {}) as Partial<UseScheduleDataReturn>;

  // Resolve service name and color from serviceId / serviceType / booking data
  const resolvedServiceId =
    booking.serviceId !== undefined && booking.serviceId !== null
      ? String(booking.serviceId).trim()
      : "";

  const serviceName =
    booking.serviceName?.trim() ||
    (resolvedServiceId ? serviceNameMap[resolvedServiceId] : "") ||
    serviceNameMap[(booking as any)?.serviceTypeId] ||
    booking.serviceType ||
    "Service";

  const serviceColor =
    booking.serviceColor ||
    (resolvedServiceId ? serviceColorMap[resolvedServiceId] : null) ||
    serviceColorMap[serviceName] ||
    serviceColorMap[booking.serviceType] ||
    serviceColors[serviceName] ||
    serviceColors[booking.serviceType] ||
    "#9ca3af";

  const pulseHighlightStyle: React.CSSProperties | undefined = pulseHighlight
    ? { borderColor: serviceColor, borderWidth: 2, boxShadow: `0 0 6px 2px ${serviceColor}` }
    : undefined;
  const pulseHighlightClass = pulseHighlight ? "animate-pulse" : "";

  const getRoomName = (
    roomIdOrName: string | undefined,
    pharmacyRoomsList: { id: string; name: string }[],
  ): string => {
    if (!roomIdOrName) return "";
    const raw = String(roomIdOrName).trim();
    if (!raw) return "";

    const foundById = pharmacyRoomsList?.find((room) => String(room.id) === raw);
    if (foundById) return foundById.name;

    const foundByName = pharmacyRoomsList?.find(
      (room) => String(room.name).trim().toLowerCase() === raw.toLowerCase(),
    );
    if (foundByName) return foundByName.name;

    const nameFromMap = (s as any)?.roomNameMap?.[raw];
    if (nameFromMap) return nameFromMap;

    if (raw.match(/^[0-9a-fA-F]{24}$/)) return "";
    return raw;
  };

  const resolvedRoomName =
    getRoomName((booking as any).roomId, pharmacyRooms || []) ||
    getRoomName(booking.room, pharmacyRooms || []);

  const roomName =
    resolvedRoomName && !resolvedRoomName.match(/^[0-9a-fA-F]{24}$/)
      ? resolvedRoomName
      : "";
  // Check if patient is partially matched
  useEffect(() => {
    const checkPatientMatch = async () => {
      try {
        const patients = await patientService.getPatients(
          booking.patientName,
          5,
        );
        const matchedPatient = patients.find(
          (p) => p.name === booking.patientName,
        );
        if (matchedPatient && matchedPatient.isPdsVerified === false) {
          setIsPartialMatch(true);
        } else {
          setIsPartialMatch(false);
        }
      } catch (e) {
        setIsPartialMatch(false);
      }
    };
    checkPatientMatch();
  }, [booking.patientName]);

  // Format time range with different levels of detail
  const formatTimeRange = (
    startTime: string,
    duration: number,
    level: "minimal" | "compact" | "full" = "compact",
  ) => {
    // Parse start time (e.g., "09:00" or "14:30")
    const [startHour, startMinute] = startTime.split(":").map(Number);

    // Calculate end time
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = startTotalMinutes + duration;
    const endHour = Math.floor(endTotalMinutes / 60) % 24;
    const endMinute = endTotalMinutes % 60;

    const startPeriod = startHour >= 12 ? "pm" : "am";
    const endPeriod = endHour >= 12 ? "pm" : "am";
    const hour12Start =
      startHour === 0 ? 12 : startHour > 12 ? startHour - 12 : startHour;
    const hour12End =
      endHour === 0 ? 12 : endHour > 12 ? endHour - 12 : endHour;

    // Minimal: Just start hour (e.g., "12" or "2p")
    if (level === "minimal") {
      return startMinute === 0
        ? `${hour12Start}`
        : `${hour12Start}:${startMinute.toString().padStart(2, "0")}`;
    }

    // Compact: Short range (e.g., "12-1pm" or "12:30-1pm")
    if (level === "compact") {
      const startStr =
        startMinute === 0
          ? `${hour12Start}`
          : `${hour12Start}:${startMinute.toString().padStart(2, "0")}`;
      const endStr =
        endMinute === 0
          ? `${hour12End}`
          : `${hour12End}:${endMinute.toString().padStart(2, "0")}`;

      if (startPeriod === endPeriod) {
        return `${startStr}-${endStr}${endPeriod}`;
      } else {
        return `${startStr}${startPeriod}-${endStr}${endPeriod}`;
      }
    }

    // Full: Complete range with all details (e.g., "12:00pm-1:00pm")
    const startStr = `${hour12Start}:${startMinute.toString().padStart(2, "0")}${startPeriod}`;
    const endStr = `${hour12End}:${endMinute.toString().padStart(2, "0")}${endPeriod}`;
    return `${startStr}-${endStr}`;
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(booking.id);
  };

  const handleBookingClick = () => {
    // Prevent click from firing immediately after drag ends
    if (justDraggedRef.current) {
      return;
    }
    onEdit(booking.id, booking);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    setIsDragging(true);
    justDraggedRef.current = false; // Reset on drag start
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(booking));
    if (onDragStart) {
      onDragStart(booking);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    justDraggedRef.current = true;

    // Reset the flag after a short delay to allow normal clicks again
    setTimeout(() => {
      justDraggedRef.current = false;
    }, 150);
  };

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeStartY(e.clientY);
    setTempDuration(booking.duration);
  };

  const handleResizeMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaY = e.clientY - resizeStartY;
      // Calculate pixels per minute based on actual row heights:
      // Day view (large): 56px per hour (60 min) = 0.93 px/minute
      // Week view (micro/small): 56px per half-hour (30 min) = 1.87 px/minute
      const pixelsPerMinute = size === "large" ? 56 / 60 : 56 / 30;
      const minutesChange = deltaY / pixelsPerMinute;

      // Round to nearest 5 minutes for smoother increments
      const roundedChange = Math.round(minutesChange / 5) * 5;
      const newDuration = Math.max(15, booking.duration + roundedChange); // Minimum 15 minutes

      setTempDuration(newDuration);
    },
    [isResizing, resizeStartY, booking.duration, size],
  );

  const handleResizeEnd = React.useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      setIsResizing(false);

      if (tempDuration !== booking.duration && onDurationChange) {
        onDurationChange(booking.id, tempDuration);
      }
    },
    [isResizing, tempDuration, booking.duration, booking.id, onDurationChange],
  );

  // Add/remove event listeners for resize
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const avatarSize =
    size === "small" ? "w-5 h-5" : size === "medium" ? "w-6 h-6" : "w-10 h-10";
  const deleteButtonSize =
    size === "small" ? "h-3 w-3" : size === "medium" ? "h-4 w-4" : "h-6 w-6";
  const deleteIconSize =
    size === "small" ? "w-2 h-2" : size === "medium" ? "w-2 h-2" : "w-3 h-3";
  const preConsultIconSize =
    size === "small" ? "w-3 h-3" : size === "medium" ? "w-3 h-3" : "w-4 h-4";

  // Calculate dynamic height for visual resize feedback
  const calculateHeight = (duration: number) => {
    // Day view (large): 56px per hour (56px per 60min)
    // Week view (small): 56px per 30min = 112px per hour
    const pixelsPerHour = size === "large" ? 56 : 112;
    const hours = duration / 60;
    const calculatedHeight = hours * pixelsPerHour;

    // Minimum height constraints
    const minHeight = size === "large" ? 40 : 30;
    return Math.max(minHeight, calculatedHeight);
  };

  // Use temp duration during resize, otherwise use booking duration
  const currentDuration = isResizing ? tempDuration : booking.duration;

  // Always calculate height when resize is enabled to support persistent visual spanning
  const dynamicHeight = enableResize
    ? calculateHeight(currentDuration)
    : undefined;

  // Use absolute positioning for multi-slot appointments (duration > 30 min) or during resize
  const isMultiSlot = currentDuration > 30;
  const shouldUseAbsolute = enableResize && (isResizing || isMultiSlot);

  // Determine icon color based on service color background
  // Light colors need dark icons, dark colors need light icons
  const isLightColor = (color: string) => {
    // Convert hex to RGB and calculate luminance
    const hex = color.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };
  const iconColor = isLightColor(serviceColor) ? "text-gray-900" : "text-white";

  // Get initials helper
  const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name?.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name?.substring(0, 2).toUpperCase();
  };

  // Pharmacist avatar and initials
  const pharmacistResource = (s?.pharmacistResources || []).find(
    (r: any) =>
      r?.resourceType === 1 &&
      (r?.name?.trim().toLowerCase() === booking?.pharmacist?.trim().toLowerCase() ||
        String(r?.id) === String(booking?.pharmacist) ||
        (booking as any)?.pharmacistId === r?.id ||
        (booking as any)?.userId === r?.id),
  );

  const avatarSrc =
    pharmacistResource?.avatar ||
    (booking as any)?.pharmacistAvatar ||
    (booking as any)?.avatar;

  const pharmacistInitials = booking?.pharmacist
    ? getInitials(booking?.pharmacist)
    : "";

  // Hover card content component
  const HoverCardInfo = () => {
    // Profiles are no longer hardcoded; data comes from API
    const profile = null;

    return (
      <div
        className={`space-y-3 ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
      >
        {/* Patient Info */}
        <div className="flex items-start gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
          <div
            className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-sm font-semibold text-white rounded-full"
            style={{ backgroundColor: serviceColor }}
          >
            {getInitials(booking.patientName)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">
              {booking.patientName}
            </div>
            <div
              className={`text-xs flex items-center gap-1 mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              <Clock className="w-3 h-3" />
              {formatTimeRange(booking.time, booking.duration, "full")}
            </div>
          </div>
        </div>

        {/* Service Type */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: serviceColor }}
            />
            <span className="text-sm font-medium">{serviceName}</span>
          </div>
        </div>

        {/* Practitioner */}
        <div className="flex items-center gap-2">
          <User
            className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          />
          <div className="flex items-center flex-1 min-w-0 gap-2">
            {profile?.avatar && (
              <img
                src={profile.avatar}
                alt={booking.pharmacist}
                className="object-cover w-5 h-5 rounded-full"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{booking.pharmacist}</div>
              {profile?.specialty && (
                <div
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {profile.specialty}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Room */}
        {roomName && (
          <div className="flex items-center gap-2">
            <MapPin
              className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
            <span className="text-sm">{roomName}</span>
          </div>
        )}

        {/* Pharmacy */}
        {booking.otherPharmacyName && (
          <div className="flex items-center gap-2">
            <Building2
              className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
              {booking.otherPharmacyName}
            </span>
          </div>
        )}

        {/* Status */}
        {booking.status && (
          <div className="flex items-center gap-2">
            <CheckCircle2
              className={`w-4 h-4 ${
                booking.status === "Completed"
                  ? "text-green-500"
                  : booking.status === "Started"
                    ? "text-blue-500"
                    : booking.status === "Cancelled"
                      ? "text-red-500"
                      : isDarkMode
                        ? "text-gray-400"
                        : "text-gray-500"
              }`}
            />
            <span
              className={`text-sm px-2 py-0.5 rounded-full ${
                booking.status === "Completed"
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  : booking.status === "Started"
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                    : booking.status === "Cancelled"
                      ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                      : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
              }`}
            >
              {booking.status}
            </span>
          </div>
        )}

        {/* Pre-Consultation Status */}
        {booking.preConsultation && (
          <div
            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
              isDarkMode
                ? "bg-green-900/20 hover:bg-green-900/30 border border-green-700"
                : "bg-green-50 hover:bg-green-100 border border-green-200"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setShowPreConsult(true);
            }}
          >
            <FileText
              className={`w-4 h-4 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
            />
            <div className="flex-1">
              <div
                className={`text-sm font-medium ${isDarkMode ? "text-green-300" : "text-green-800"}`}
              >
                Pre-Consultation Completed
              </div>
              <div
                className={`text-xs ${isDarkMode ? "text-green-400/70" : "text-green-600/70"}`}
              >
                Completed{" "}
                {new Date(
                  booking.preConsultation.completedAt,
                ).toLocaleDateString()}{" "}
                • Click to view
              </div>
            </div>
            <CheckCircle2
              className={`w-5 h-5 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
            />
          </div>
        )}

        {/* Tasks to Complete */}
        {booking.tasksToComplete && booking.tasksToComplete.length > 0 && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <div
              className={`text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Tasks to Complete:
            </div>
            <div className="flex flex-wrap gap-1">
              {booking.tasksToComplete.map((task: any, idx: number) => {
                const label =
                  task?.label ||
                  task?.text ||
                  task?.name ||
                  (typeof task === "string" ? task : String(task));
                return (
                  <span
                    key={task?.id || idx}
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      isDarkMode
                        ? "bg-blue-900/30 text-blue-300"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Vaccines */}
        {((booking.vaccinesToAdminister && booking.vaccinesToAdminister.length > 0) ||
          (booking.vaccinesAdminister && booking.vaccinesAdminister.length > 0)) && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Syringe
                className={`w-3 h-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
              <span
                className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Vaccines:
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {(booking.vaccinesToAdminister || booking.vaccinesAdminister || []).map(
                (vaccine: any, idx: number) => {
                  const name =
                    vaccine?.vaccineName ||
                    vaccine?.VaccineName ||
                    vaccine?.name ||
                    vaccine?.Name ||
                    (typeof vaccine === "string" ? vaccine : String(vaccine));
                  return (
                    <span
                      key={vaccine?.id || vaccine?.Id || idx}
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isDarkMode
                          ? "bg-amber-900/30 text-amber-300"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {name}
                    </span>
                  );
                },
              )}
            </div>
          </div>
        )}

        {/* Contact Info */}
        {(booking.contactNumber || booking.email) && (
          <div
            className={`pt-2 border-t border-gray-200 dark:border-gray-700 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {booking.contactNumber && <div>📞 {booking.contactNumber}</div>}
            {booking.email && <div>✉️ {booking.email}</div>}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              const startFn = onStartConsultation || handleStartConsultation;
              if (startFn) {
                startFn(booking);
              }
            }}
            className={`flex-1 h-8 ${
              booking.status === "Started"
                ? "bg-blue-500 hover:bg-blue-600 text-white"
                : "bg-green-500 hover:bg-green-600 text-white"
            }`}
          >
            <Play className="w-3 h-3 mr-1" />
            {booking.status === "Started" ? "Continue" : "Start"}
          </Button>

          {booking.eventStatus !== 5 && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                if (handleMarkAsNoShow) {
                  handleMarkAsNoShow(booking);
                }
              }}
              className={`flex-1 h-8 ${
                isDarkMode
                  ? "border-red-600 text-red-400 hover:bg-red-900/20"
                  : "border-red-500 text-red-600 hover:bg-red-50"
              }`}
            >
              <UserX className="w-3 h-3 mr-1" />
              No Show
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(booking.id, booking);
            }}
            className={`h-8 w-8 p-0 ${
              isDarkMode
                ? "hover:bg-gray-700 text-gray-300"
                : "hover:bg-gray-100 text-gray-600"
            }`}
            title="Edit appointment"
          >
            <Edit className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  // Pre-Consultation Dialog (shared across all card variants and hover card)
  const PreConsultDialog = () => {
    if (!booking.preConsultation) return null;

    return (
      <Dialog open={showPreConsult} onOpenChange={setShowPreConsult}>
        <DialogContent
          className={`max-w-2xl ${
            isDarkMode ? "bg-gray-800 text-white" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle
              className={isDarkMode ? "text-white" : "text-gray-900"}
            >
              Pre-Consultation Questionnaire
            </DialogTitle>
            <p
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Patient: {booking.patientName} • Completed on{" "}
              {new Date(booking.preConsultation.completedAt).toLocaleString()}
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            {booking.preConsultation.responses.map((response, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <p
                  className={`text-sm font-semibold mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {response.question}
                </p>
                {response.type === "image" && Array.isArray(response.answer) ? (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {response.answer.map((imageUrl: string, imgIdx: number) => (
                      <div
                        key={imgIdx}
                        className={`relative rounded-lg overflow-hidden border ${
                          isDarkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <img
                          src={imageUrl}
                          alt={`Uploaded image ${imgIdx + 1}`}
                          className="object-cover w-full h-32"
                          loading="lazy"
                        />
                        <div
                          className={`absolute bottom-0 left-0 right-0 px-2 py-1 text-xs ${
                            isDarkMode
                              ? "bg-gray-900/80 text-gray-300"
                              : "bg-white/80 text-gray-600"
                          }`}
                        >
                          Image {imgIdx + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {Array.isArray(response.answer)
                      ? response.answer.join(", ")
                      : response.answer}
                  </p>
                )}
              </div>
            ))}

            {/* Disclaimer Section */}
            {booking.preConsultation.disclaimer && (
              <div
                className={`p-4 rounded-lg border-2 ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-amber-50 border-amber-200"
                }`}
              >
                <h4
                  className={`text-sm font-semibold mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Patient Declaration & Consent
                </h4>
                <p
                  className={`text-sm mb-4 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {booking.preConsultation.disclaimer.text}
                </p>
                {booking.preConsultation.disclaimer.signed && (
                  <div
                    className={`pt-3 border-t ${
                      isDarkMode ? "border-gray-600" : "border-amber-200"
                    }`}
                  >
                    <div className="flex items-end justify-between gap-4">
                      <div>
                        <p
                          className={`text-xs mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Patient Signature
                        </p>
                        <p
                          className="text-xl font-cursive"
                          style={{
                            fontFamily: "'Dancing Script', cursive",
                          }}
                        >
                          {booking.preConsultation.disclaimer.signature}
                        </p>
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-xs mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Signed On
                        </p>
                        <p
                          className={`text-sm ${
                            isDarkMode ? "text-gray-200" : "text-gray-800"
                          }`}
                        >
                          {new Date(
                            booking.preConsultation.disclaimer.signedAt,
                          ).toLocaleDateString("en-GB")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Accordion item variant - perfectly matching screenshot 2 design with clean borders, shadows, padding, and dark mode support
  if (size === "accordion") {
    return (
      <>
        <PreConsultDialog />
        <HoverCard openDelay={300} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div
              className="relative w-full group pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                draggable={true}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                className={`relative flex flex-col justify-center pl-3.5 pr-2.5 py-2.5 rounded-lg border text-xs transition-all duration-150 overflow-hidden cursor-pointer shadow-xs hover:shadow-sm ${
                  isDarkMode
                    ? "bg-gray-800/95 border-gray-700 text-gray-200 hover:bg-gray-800"
                    : "bg-white border-gray-200/90 text-gray-900 hover:bg-gray-50/80"
                } ${isPartialMatch ? "ring-1 ring-yellow-500" : ""} ${isDragging ? "opacity-50" : ""} ${
                  showNotification ? "animate-[pulse_0.5s_ease-in-out_2]" : ""
                } ${pulseHighlightClass}`}
                style={pulseHighlightStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookingClick();
                }}
              >
                {/* Colored vertical accent bar on left edge */}
                <div
                  className="absolute top-0 bottom-0 left-0 w-1 rounded-l"
                  style={{ backgroundColor: serviceColor }}
                />

                {/* Notification animation overlay */}
                {showNotification && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center bg-green-500/20 rounded animate-[fadeIn_0.3s_ease-in,fadeOut_0.5s_ease-out_1.5s]">
                    <div className="bg-green-500 text-white rounded-full p-1 shadow-lg animate-[scaleIn_0.3s_ease-out]">
                      {notificationType === "message" ? (
                        <Send className="w-3 h-3" />
                      ) : (
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                )}

                {isPartialMatch && (
                  <div className="absolute top-1 right-1 bg-yellow-500 rounded-full p-0.5 shadow-md z-10">
                    <AlertTriangle className="w-2.5 h-2.5 text-white" />
                  </div>
                )}

                {/* 3-line layout: Patient -> Timing with Avatar -> Service */}
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  {/* Line 1: Patient Name & Pharmacy Label */}
                  <div className="flex items-center justify-between gap-1.5 leading-tight min-w-0">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-semibold text-xs text-gray-900 dark:text-gray-100 truncate">
                        {booking.patientName}
                      </span>
                      {booking.otherPharmacyName && (
                        <span className="inline-flex items-center justify-center text-[10px] font-medium px-2.5 py-1 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex-shrink-0 truncate max-w-[140px] leading-none shadow-xs">
                          {booking.otherPharmacyName}
                        </span>
                      )}
                    </div>
                    {onDelete && (
                      <button
                        onClick={handleDelete}
                        className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100"
                        title="Delete appointment"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {/* Line 2: Timing with Avatar */}
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 leading-tight min-w-0 mt-0.5">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={booking.pharmacist || "Pharmacist"}
                        className="w-4 h-4 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-600 shadow-xs"
                      />
                    ) : pharmacistInitials ? (
                      <div
                        className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-white font-medium text-[8px]"
                        style={{ backgroundColor: serviceColor }}
                        title={booking.pharmacist}
                      >
                        {pharmacistInitials}
                      </div>
                    ) : null}
                    <span className="truncate font-normal">
                      {formatTimeRange(
                        booking.time,
                        booking.duration,
                        "compact",
                      )}
                    </span>
                    {booking.preConsultation && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPreConsult(true);
                        }}
                        className="flex-shrink-0 ml-auto p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        title="Pre-consultation details"
                      >
                        <FileText className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {/* Line 3: Service type */}
                  <div className="text-[11px] text-gray-600 dark:text-gray-300 leading-tight truncate font-normal mt-0.5">
                    {serviceName}
                  </div>
                </div>
              </div>
            </div>
          </HoverCardTrigger>
          <HoverCardContent
            side="right"
            align="start"
            className={`w-80 z-20 ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
          >
            <HoverCardInfo />
          </HoverCardContent>
        </HoverCard>
      </>
    );
  }

  // Micro variant - compact for week day view showing patient name at top, timing in middle, service below
  if (size === "micro") {
    // Calculate dynamic height for visual resize feedback in micro mode
    const calculateMicroHeight = (duration: number) => {
      // Week view: 56px per 30min = 112px per hour
      const pixelsPerHour = 112;
      const hours = duration / 60;
      const calculatedHeight = hours * pixelsPerHour;
      return Math.max(38, calculatedHeight); // Minimum 38px
    };

    const microDynamicHeight = enableResize
      ? calculateMicroHeight(currentDuration)
      : undefined;
    const microIsMultiSlot = currentDuration > 30;
    const microShouldUseAbsolute =
      enableResize && (isResizing || microIsMultiSlot);

    return (
      <>
        <PreConsultDialog />
        <HoverCard openDelay={300} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div
              className={`group w-full relative z-20 pointer-events-auto ${isResizing ? "transition-none" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                draggable={true}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                className={`${microShouldUseAbsolute ? "absolute top-0 left-0 right-0 shadow-lg" : "relative"} flex flex-col justify-center pl-2.5 pr-1.5 py-1 rounded text-xs border ${isDarkMode ? "bg-gray-800 border-gray-700 text-gray-200" : "bg-white border-gray-200 text-gray-900"} ${isPartialMatch ? "ring-1 ring-yellow-500" : ""} ${isResizing ? "cursor-ns-resize" : "cursor-pointer"} hover:shadow-md transition-shadow w-full overflow-hidden ${isDragging ? "opacity-50" : ""} ${showNotification ? "animate-[pulse_0.5s_ease-in-out_2]" : ""} ${pulseHighlightClass}`}
                style={
                  microDynamicHeight
                    ? { height: `${microDynamicHeight}px`, minHeight: "38px", ...pulseHighlightStyle }
                    : pulseHighlightStyle
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookingClick();
                }}
                title=""
              >
                {/* Colored vertical bar for service type */}
                <div
                  className="absolute top-0 bottom-0 left-0 w-1 rounded-l"
                  style={{ backgroundColor: serviceColor }}
                />

                {/* Notification animation overlay */}
                {showNotification && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center bg-green-500/20 rounded animate-[fadeIn_0.3s_ease-in,fadeOut_0.5s_ease-out_1.5s]">
                    <div className="bg-green-500 text-white rounded-full p-1 shadow-lg animate-[scaleIn_0.3s_ease-out]">
                      {notificationType === "message" ? (
                        <Send className="w-3 h-3" />
                      ) : (
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                )}
                {isPartialMatch && (
                  <div className="absolute top-0.5 right-0.5 bg-yellow-500 rounded-full p-0.5 shadow-md z-10">
                    <AlertTriangle className="w-2 h-2 text-white" />
                  </div>
                )}
                {isResizing &&
                  enableResize &&
                  (() => {
                    const durationChange = tempDuration - booking.duration;
                    const sign = durationChange >= 0 ? "+" : "";
                    return (
                      <div className="absolute top-0.5 left-0.5 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded-full z-10">
                        {sign}
                        {durationChange}m
                      </div>
                    );
                  })()}
                {/* Responsive content - 3-line layout (Patient -> Timing with Avatar -> Service) */}
                <div className="flex flex-col gap-0.5 min-h-0 flex-1 justify-center">
                  {/* Line 1: Patient Name & Pharmacy Label */}
                  <div className="flex items-center justify-between gap-1 font-semibold text-[11px] leading-tight min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <span className="truncate">{booking.patientName}</span>
                      {booking.otherPharmacyName && (
                        <span className="inline-flex items-center justify-center text-[9px] font-medium px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex-shrink-0 truncate max-w-[95px] leading-none shadow-xs">
                          {booking.otherPharmacyName}
                        </span>
                      )}
                    </div>
                    {onDelete && (
                      <button
                        onClick={handleDelete}
                        className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Delete appointment"
                      >
                        <Trash2 className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>

                  {/* Line 2: Timing with Avatar */}
                  <div className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300 leading-tight min-w-0">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={booking.pharmacist || "Pharmacist"}
                        className="w-3.5 h-3.5 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-600 shadow-xs"
                      />
                    ) : pharmacistInitials ? (
                      <div
                        className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 text-white font-medium text-[8px]"
                        style={{ backgroundColor: serviceColor }}
                        title={booking.pharmacist}
                      >
                        {pharmacistInitials}
                      </div>
                    ) : null}
                    <span className="truncate font-normal">
                      {formatTimeRange(
                        booking.time,
                        currentDuration,
                        "compact",
                      )}
                    </span>
                    {booking.preConsultation && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPreConsult(true);
                        }}
                        className="flex-shrink-0 ml-auto p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        title="Pre-consultation details"
                      >
                        <FileText className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {/* Line 3: Service type */}
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate font-normal">
                    {serviceName}
                  </div>
                </div>

                {!isResizing && isPartialMatch && (
                  <div className="absolute top-1 right-1">
                    <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0" />
                  </div>
                )}

                {/* Resize handle for micro cards */}
                {enableResize && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20 ${
                      isResizing ? "opacity-100 bg-black/10" : ""
                    }`}
                    onMouseDown={handleResizeStart}
                  >
                    <div
                      className={`w-6 h-0.5 rounded-full bg-white ${isResizing ? "opacity-100 shadow-lg" : "opacity-60"}`}
                    />
                  </div>
                )}
              </div>
            </div>
          </HoverCardTrigger>
          <HoverCardContent
            side="right"
            align="start"
            className={`w-80 z-20 ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
          >
            <HoverCardInfo />
          </HoverCardContent>
        </HoverCard>
      </>
    );
  }

  if (size === "large") {
    return (
      <>
        <PreConsultDialog />
        <HoverCard openDelay={300} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div
              className={`group w-full relative z-20 pointer-events-auto ${isResizing ? "transition-none" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                draggable={true}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                className={`${shouldUseAbsolute ? "absolute top-0 left-0 right-0 shadow-lg" : "relative"} pl-3.5 pr-3 py-2.5 rounded-lg border ${isDarkMode ? "bg-gray-800 border-gray-700 text-gray-200" : "bg-white border-gray-200 text-gray-900"} ${isResizing ? "cursor-ns-resize" : "cursor-pointer"} hover:shadow-md transition-shadow overflow-hidden flex flex-col justify-center ${isPartialMatch ? "ring-2 ring-yellow-500 ring-offset-1" : ""} ${isDragging ? "opacity-50" : ""} ${showNotification ? "animate-[pulse_0.5s_ease-in-out_2]" : ""} ${pulseHighlightClass}`}
                style={
                  dynamicHeight ? { height: `${dynamicHeight}px`, ...pulseHighlightStyle } : pulseHighlightStyle
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookingClick();
                }}
                title=""
              >
                {/* Colored vertical bar for service type */}
                <div
                  className="absolute top-0 bottom-0 left-0 w-1 rounded-l"
                  style={{ backgroundColor: serviceColor }}
                />

                {/* Notification animation overlay */}
                {showNotification && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center bg-green-500/20 rounded-lg animate-[fadeIn_0.3s_ease-in,fadeOut_0.5s_ease-out_1.5s]">
                    <div className="bg-green-500 text-white rounded-full p-2 shadow-lg animate-[scaleIn_0.3s_ease-out]">
                      {notificationType === "message" ? (
                        <Send className="w-5 h-5" />
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                )}
                {isPartialMatch && (
                  <div className="absolute p-1 bg-yellow-500 rounded-full shadow-lg top-1 right-1">
                    <AlertTriangle className="w-3 h-3 text-white" />
                  </div>
                )}

                {/* Content: 3-line layout (Patient -> Timing with Avatar -> Service) */}
                <div className="flex flex-col gap-1 min-h-0 flex-1 justify-center">
                  {/* Line 1: Patient Name & Pharmacy Label */}
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="font-semibold text-sm leading-tight text-gray-900 dark:text-white truncate">
                        {booking.patientName}
                      </div>
                      {booking.otherPharmacyName && (
                        <span className="inline-flex items-center justify-center text-[11px] font-medium px-3 py-1 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex-shrink-0 truncate max-w-[160px] leading-none shadow-xs">
                          {booking.otherPharmacyName}
                        </span>
                      )}
                    </div>
                    {onDelete && (
                      <button
                        onClick={handleDelete}
                        className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Delete appointment"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Line 2: Timing with Avatar */}
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 leading-tight min-w-0">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={booking.pharmacist || "Pharmacist"}
                        className="w-4 h-4 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-600 shadow-xs"
                      />
                    ) : pharmacistInitials ? (
                      <div
                        className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-white font-medium text-[9px]"
                        style={{ backgroundColor: serviceColor }}
                        title={booking.pharmacist}
                      >
                        {pharmacistInitials}
                      </div>
                    ) : null}
                    <span className="font-normal truncate">
                      {formatTimeRange(
                        booking.time,
                        currentDuration,
                        "compact",
                      )}
                    </span>
                    <span className="text-[11px] opacity-75">
                      ({isResizing ? tempDuration : booking.duration} min)
                    </span>
                    {booking.preConsultation && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowPreConsult(true);
                        }}
                        className="flex-shrink-0 ml-auto p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        title="Pre-consultation details"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Line 3: Service type */}
                  <div className="text-xs text-gray-500 dark:text-gray-400 leading-tight truncate">
                    {serviceName}
                  </div>

                  {booking.notes && (
                    <div className="text-xs italic text-gray-500 dark:text-gray-400 truncate">
                      Notes: {booking.notes}
                    </div>
                  )}
                </div>

                {/* Resize handle */}
                {enableResize && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20 ${
                      isResizing ? "opacity-100 bg-black/10" : ""
                    }`}
                    onMouseDown={handleResizeStart}
                  >
                    <div
                      className={`w-12 h-1 rounded-full ${
                        isResizing
                          ? "bg-white shadow-lg"
                          : iconColor === "text-gray-900"
                            ? "bg-gray-900"
                            : "bg-white"
                      } ${isResizing ? "opacity-100" : "opacity-60"}`}
                    />
                  </div>
                )}
              </div>
            </div>
          </HoverCardTrigger>
          <HoverCardContent
            side="right"
            align="start"
            className={`w-80 z-20 ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
          >
            <HoverCardInfo />
          </HoverCardContent>
        </HoverCard>
      </>
    );
  }

  // Medium/Small card variant (Month view & Default)
  return (
    <>
      <PreConsultDialog />
      <HoverCard openDelay={300} closeDelay={100}>
        <HoverCardTrigger asChild>
          <div
            className={`group w-full relative z-20 pointer-events-auto ${isResizing ? "transition-none" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              draggable={true}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              className={`${shouldUseAbsolute ? "absolute top-0 left-0 right-0 shadow-lg" : "relative"} text-xs pl-2.5 pr-1.5 py-1 rounded border ${isDarkMode ? "bg-gray-800 border-gray-700 text-gray-200" : "bg-white border-gray-200 text-gray-900"} ${isResizing ? "cursor-ns-resize" : "cursor-pointer"} hover:shadow-md transition-shadow overflow-hidden flex flex-col justify-center ${isPartialMatch ? "ring-2 ring-yellow-500 ring-offset-1" : ""} ${isDragging ? "opacity-50" : ""} ${showNotification ? "animate-[pulse_0.5s_ease-in-out_2]" : ""} ${pulseHighlightClass}`}
              style={
                dynamicHeight ? { height: `${dynamicHeight}px`, ...pulseHighlightStyle } : pulseHighlightStyle
              }
              onClick={(e) => {
                e.stopPropagation();
                handleBookingClick();
              }}
              title=""
            >
              {/* Colored vertical bar for service type */}
              <div
                className="absolute top-0 bottom-0 left-0 w-1 rounded-l"
                style={{ backgroundColor: serviceColor }}
              />

              {/* Notification animation overlay */}
              {showNotification && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center bg-green-500/20 rounded animate-[fadeIn_0.3s_ease-in,fadeOut_0.5s_ease-out_1.5s]">
                  <div className="bg-green-500 text-white rounded-full p-1.5 shadow-lg animate-[scaleIn_0.3s_ease-out]">
                    {notificationType === "message" ? (
                      <Send className="w-4 h-4" />
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              )}
              {isPartialMatch && (
                <div className="absolute top-0.5 right-0.5 bg-yellow-500 rounded-full p-0.5 shadow-md z-10">
                  <AlertTriangle className="w-2.5 h-2.5 text-white" />
                </div>
              )}
              {isResizing &&
                enableResize &&
                (() => {
                  const durationChange = tempDuration - booking.duration;
                  const sign = durationChange >= 0 ? "+" : "";
                  return (
                    <div className="absolute top-0.5 left-0.5 bg-black/80 text-white text-xs px-2 py-0.5 rounded-full z-10">
                      {sign}
                      {durationChange}m
                    </div>
                  );
                })()}

              {/* Content: 3-line layout (Patient -> Timing with Avatar -> Service) */}
              <div className="flex flex-col gap-0.5 min-h-0 flex-1 justify-center">
                {/* Line 1: Patient Name & Pharmacy Label */}
                <div className="flex items-center justify-between gap-1.5 font-semibold text-xs leading-tight min-w-0">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="truncate">{booking.patientName}</span>
                    {booking.otherPharmacyName && (
                      <span className="inline-flex items-center justify-center text-[10px] font-medium px-2.5 py-1 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex-shrink-0 truncate max-w-[130px] leading-none shadow-xs">
                        {booking.otherPharmacyName}
                      </span>
                    )}
                  </div>
                  {onDelete && (
                    <button
                      onClick={handleDelete}
                      className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Delete appointment"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>

                {/* Line 2: Timing with Avatar */}
                <div className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300 leading-tight min-w-0">
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt={booking.pharmacist || "Pharmacist"}
                      className="w-3.5 h-3.5 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-600 shadow-xs"
                    />
                  ) : pharmacistInitials ? (
                    <div
                      className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 text-white font-medium text-[8px]"
                      style={{ backgroundColor: serviceColor }}
                      title={booking.pharmacist}
                    >
                      {pharmacistInitials}
                    </div>
                  ) : null}
                  <span className="truncate font-normal">
                    {formatTimeRange(
                      booking.time,
                      currentDuration,
                      "compact",
                    )}
                  </span>
                  {booking.preConsultation && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPreConsult(true);
                      }}
                      className="flex-shrink-0 ml-auto p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                      title="Pre-consultation details"
                    >
                      <FileText className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Line 3: Service type */}
                <div className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight truncate font-normal">
                  {serviceName}
                </div>
              </div>

              {/* Resize handle for small/medium cards */}
              {enableResize && (
                <div
                  className={`absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20 ${
                    isResizing ? "opacity-100 bg-black/10" : ""
                  }`}
                  onMouseDown={handleResizeStart}
                >
                  <div
                    className={`w-8 h-1 rounded-full ${
                      isResizing
                        ? "bg-white shadow-lg"
                        : iconColor === "text-gray-900"
                          ? "bg-gray-900"
                          : "bg-white"
                    } ${isResizing ? "opacity-100" : "opacity-60"}`}
                  />
                </div>
              )}
            </div>
          </div>
        </HoverCardTrigger>
        <HoverCardContent
          side="right"
          align="start"
          className={`w-80 z-20 ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
        >
          <HoverCardInfo />
        </HoverCardContent>
      </HoverCard>
    </>
  );
}
